<template>
    <div class="vardplatsfakturering">
        <div class="header">
            <h1>Vårdplatsfakturering</h1>
        </div>
        <search-filter
            v-model="filterValues"
            @input="handleSearch"
            @filterUpdated="handleFilterValuesUpdated"
            :fields="filterFields"
            :isOpen="true"
            :enableSubmit="true"
        >
            <template v-slot:fields>
                <FormulateInput type="datepicker" name="uttid_eller_struken__gte" label="Utskrivningsdatum fr.o.m." />
                <FormulateInput type="datepicker" name="uttid_eller_struken__lte" label="Utskrivningsdatum t.o.m." />
                <FormulateInput
                    type="vueselect"
                    name="vardplatsbestallning__vardgivare"
                    label="Extern vårdgivare"
                    :options="externa_vardgivare"
                    v-model="selectedExternVardgivare"
                    @input="getExternaVardpersoner"
                />
                <FormulateInput
                    type="vueselect"
                    name="vardplatsbestallning__operator"
                    label="Extern vårdperson"
                    :options="externa_vardpersoner"
                    v-model="selectedExternVardperson"
                />
                <FormulateInput type="checkbox" name="show_person_id_and_fullname" label="Visa PersonID och namn" />
            </template>
        </search-filter>

        <template>
            <Button
                type="button"
                role="button"
                size="secondary"
                @click="downloadExcelFile"
                :disabled="buttonShouldBeDisabled"
                v-tooltip="{
                    content: buttonShouldBeDisabled ? disabledButtonTooltip : '',
                    theme: 'kliniken-tooltip',
                }"
            >
                <img src="@/assets/download_white.svg" />Exportera excel
            </Button>
        </template>
    </div>
</template>

<script>
    import tabIcon from "@/assets/avtal.svg";

    export default {
        // eslint-disable-next-line vue/multi-word-component-names
        name: "Vardplatsfakturering",
        tabName: "Vårdplatsfakturering",
        icon: tabIcon,
    };
</script>

<script setup>
    import { computed, ref, onMounted, defineProps, toRefs } from "vue";
    import isEqual from "lodash/isEqual";
    import { getConfig, klinikenApi, getErrorMessage } from "@/api";
    import { openDialog } from "@/utils";
    import store from "@/store";
    import SearchFilter from "@/components/SearchFilter.vue";
    import Button from "@/components/Button.vue";

    const largeSearchWarning =
        "Du har valt ett stort urval som kan ta lång tid att ladda. Vill du gå vidare med din sökning?";

    const disabledButtonTooltip = "Tillämpa sökning först";

    const props = defineProps({
        tabId: Number,
    });

    const { tabId } = toRefs(props);
    const buttonShouldBeDisabled = ref(false);
    const filterValues = ref({});
    const selectedExternVardgivare = ref(null);
    const selectedExternVardperson = ref(null);

    const externa_vardpersoner = ref([]);
    const externa_vardgivare = ref([]);
    const items = ref([]);

    const filterFields = computed(() => ({
        uttid_eller_struken__gte: { label: "Utskrivningsdatum från " },
        uttid_eller_struken__lte: { label: "Utskrivningsdatum till " },
        vardplatsbestallning__vardgivare: { label: "Extern vårdgivare ", options: externa_vardgivare.value },
        vardplatsbestallning__operator: { label: "Extern vårdperson ", options: externa_vardpersoner.value },
        show_person_id_and_fullname: { label: "PersonID och namn " },
    }));

    const handleFilterValuesUpdated = (updatedFilterValues) => {
        buttonShouldBeDisabled.value = !isEqual(updatedFilterValues, filterValues.value);
    };

    const showWarningSearchFilter = ({ action }) => {
        openDialog({
            type: "warning",
            description: largeSearchWarning,
            buttons: [
                {
                    title: "Avbryt",
                    type: "secondary",
                },
                {
                    title: "Ja, gå vidare",
                    type: "primary",
                    action,
                },
            ],
        });
    };

    const handleSearch = async (filterData) => {
        filterValues.value = filterData;

        if (Object.keys(filterData).length === 0) {
            /**
             * Show warning dialog if filter values is empty
             * Too large search, ask user if they want to continue
             */
            showWarningSearchFilter({
                action: async () => {
                    await fetchVardplatsfakturering();
                },
            });
        } else {
            await fetchVardplatsfakturering();
        }
    };

    async function downloadExcelFile() {
        try {
            const params = {
                ...filterValues.value,
                excel: true,
            };

            const exportExcelResponse = await klinikenApi.get(
                "/slutenvard/slutenvardsbesok/fakturering/",
                getConfig({
                    params,
                    responseType: "blob", // Specify arraybuffer to handle binary data
                })
            );

            const contentType = exportExcelResponse.headers["content-type"];
            const contentDisposition = exportExcelResponse.headers["content-disposition"];
            const blob = new Blob([exportExcelResponse.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.style.display = "none";
            a.href = url;
            /**
             * Extract the filename from the Content-Disposition header using regex.
             * The regex `filename="([^"]+)"` captures the filename between the quotes.
             * For example, from `attachment; filename="export.xls"`, it extracts `export.xls`.
             */
            a.download = contentDisposition.match(/filename="([^"]+)"/)[1];
            document.body.appendChild(a);
            a.click();

            window.URL.revokeObjectURL(url);
            document.body.removeChild(a); // Clean up the anchor element after download
        } catch (error) {
            openDialog(getErrorMessage(error), "error");
        }
    }

    const fetchVardplatsfakturering = async () => {
        await store.dispatch("tabs/displaySpinner", { id: tabId.value, display: true });

        try {
            const params = {
                ...filterValues.value,
            };

            const response = await klinikenApi.get(
                "/slutenvard/slutenvardsbesok/fakturering/",
                getConfig({
                    params,
                })
            );

            items.value = response.data.results;
        } catch (error) {
            openDialog("Kunde inte hämta slutenvårdsplatser.", "warning");
        } finally {
            await store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
        }
    };

    const getExternaVardgivare = async () => {
        try {
            const response = await klinikenApi.get(
                "/core/externavardgivare/",
                getConfig({
                    params: {},
                })
            );

            externa_vardgivare.value = response.data.results.map(({ id, namn }) => {
                return {
                    value: id,
                    label: namn,
                };
            });
        } catch (error) {
            openDialog("Kunde inte hämta externa vårdgivare.", "warning");
        }
    };

    const getExternaVardpersoner = async () => {
        try {
            let params = {};
            if (selectedExternVardgivare.value) {
                params = {
                    vardgivare: selectedExternVardgivare.value,
                };
            }
            const response = await klinikenApi.get("/core/externavardpersoner/", getConfig({ params: params }));

            externa_vardpersoner.value = response.data.results.map(({ id, namn }) => {
                return {
                    value: id,
                    label: namn,
                };
            });
        } catch (error) {
            openDialog("Kunde inte hämta externa vårdpersoner.", "warning");
        } finally {
            selectedExternVardperson.value = null;
        }
    };

    onMounted(async () => {
        /**
         * Hämta all nödvändig data asynkront parallellt, men vänta tills alla är klara innan vi fortsätter.
         * Parallellt innebär att alla hämtningar startar samtidigt och körs oberoende av varandra.
         * Detta görs för att minska den totala laddningstiden och förbättra prestandan.
         */
        await Promise.all([getExternaVardgivare(), getExternaVardpersoner()]);
    });
</script>

<style lang="scss" scoped>
    @import "@/style/_variables.scss";
</style>
